/**
 * Регистрация.
 *
 * Экрана не было вовсе: `signup()` звался только из приёма приглашения, а на
 * форме входа стояло «Нет учётной записи? Начните с бесплатного аудита на
 * seeno.ru» — то есть кольцо замыкалось и завести запись было нечем. Спека §4
 * начинает путь клиента с бесплатного аудита, но это **вход** в воронку, а не
 * единственная дверь: по приглашению, по прямой ссылке и с оплаченного тарифа
 * человек приходит сюда.
 *
 * Ответ сервера — `202` и письмо, **всегда**. Занятому адресу уходит письмо «на
 * вашу почту пытались зарегистрироваться», а не отказ: иначе форма регистрации
 * становится способом перебрать, кто у нас обслуживается. Поэтому у экрана два
 * состояния и ни одного «такой адрес занят».
 *
 * `organization_name` заводит организацию и делает регистрирующегося её
 * владельцем (§5). Спрашиваем его здесь, а не в онбординге: роль владельца
 * определяет, что человек увидит на первом же экране, и доспрашивать её потом
 * значит показать кабинет, который сразу же изменится.
 *
 * Отдельной галочки согласия у формы нет. Признак согласия в `SignupRequest`
 * не уезжает — поля там нет, — а галочка, о которой сервер не узнаёт, ничего
 * не доказывает (доказывание на операторе, ч. 4 ст. 9 152-ФЗ). Обработка
 * данных учётной записи опирается на исполнение договора (п. 5 ч. 1 ст. 6
 * 152-ФЗ); строка под кнопкой называет принимаемые документы. Появится поле в
 * `seeno-ru-api` (образец — `offer_consent` на оплате) — согласие вернётся
 * галочкой вместе с серверной записью.
 */

import { useState, type FormEvent } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import { AuthCard } from './AuthCard';
import { Field } from '../../ui/form';
import { ErrorNotice } from '../../ui/state';
import { resendEmailVerification, signup } from '../../api/auth';
import { ApiError, fieldErrors } from '../../api/problem';
import { PASSWORD_HINT, PASSWORD_MIN_LENGTH, shortPassword } from '../../ui/password';
import { LEGAL, LEGAL_LINK_PROPS } from '../../legal';
import { AUDIT_SESSION_KEY } from '../Audit';

export function Signup() {
  const [params] = useSearchParams();
  // Куда человек шёл до регистрации: переносим обратно на вход, чтобы
  // намерение не терялось на переходах между двумя формами.
  const from = (useLocation().state as { from?: string } | null)?.from;

  const [email, setEmail] = useState(params.get('email') ?? '');
  const [fullName, setFullName] = useState('');
  const [organization, setOrganization] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [sent, setSent] = useState(false);
  const [resent, setResent] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      // Аудит, запущенный до регистрации, присваивается тем же действием.
      // Токен происхождения положил экран `/audit`: он приходит заголовком
      // `X-Seeno-Audit-Session` один раз, в ответе на запуск. Без него человек
      // зарегистрировался бы, а свой единственный результат потерял — и
      // вернуть его было бы нечем, потому что публичный слаг правом не
      // является.
      const auditToken = sessionStorage.getItem(AUDIT_SESSION_KEY);
      await signup({
        email: email.trim(),
        password,
        ...(fullName.trim() ? { full_name: fullName.trim() } : {}),
        ...(organization.trim() ? { organization_name: organization.trim() } : {}),
        ...(auditToken ? { audit_session_token: auditToken } : {}),
      });
      setSent(true);
    } catch (caught) {
      if (caught instanceof ApiError) setFields(fieldErrors(caught.problem));
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthCard
        title="Проверьте почту"
        footer={
          <>
            Ошиблись в адресе? <Link to="/auth/signup">Начать заново</Link> ·{' '}
            <Link to="/auth/login">Войти</Link>
          </>
        }
      >
        <p className="auth-lead">
          Мы отправили письмо на <b>{email.trim()}</b>. Откройте ссылку из него — она
          подтвердит адрес и сразу впустит вас в кабинет, отдельного входа после этого не
          потребуется.
        </p>
        {/* Ответ на занятый адрес такой же, как на свободный, и это не будет
            меняться: разный ответ превращает форму регистрации в способ
            перебрать, кто у нас обслуживается. Но «одинаковый ответ» не обязан
            означать «человеку не сказали ничего». Экран называет ОБА исхода и
            даёт под каждый работающее действие: занятому адресу нужен вход или
            восстановление пароля, а не ожидание письма, которого он ждёт не про
            то. Приёмка на проде читала это состояние как тишину — потому что
            выходов из него на экране не было. */}
        <p className="auth-lead">
          Письма нет через пару минут — посмотрите в спаме. Если этот адрес уже
          зарегистрирован, письмо придёт другое: о том, что на него пытались завести
          вторую запись. Тогда регистрация не нужна — нужен вход.
        </p>
        <p className="auth-actions">
          <Link className="btn btn-ghost btn-sm" to="/auth/login">
            Войти
          </Link>
          <Link
            className="btn btn-ghost btn-sm"
            to={`/auth/forgot?email=${encodeURIComponent(email.trim())}`}
          >
            Не помню пароль
          </Link>
        </p>
        {/* Экран был тупиком: ни повтора, ни возможности исправить адрес, ни
            ссылки на вход. Опечатался в почте — адрес занят, письма не
            получить, зарегистрироваться заново нечем. Серверная ручка
            `POST /auth/email/verify/resend` при этом существует и объявлена в
            контракте, а клиент к ней не обращался ни с одного экрана. */}
        {resent ? (
          <p className="auth-lead">Письмо отправлено ещё раз. Проверьте почту.</p>
        ) : (
          <button
            className="btn btn-ghost"
            type="button"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setError(null);
              resendEmailVerification(email.trim())
                .then(() => setResent(true))
                .catch(setError)
                .finally(() => setBusy(false));
            }}
          >
            {busy ? 'Отправляем…' : 'Отправить письмо ещё раз'}
          </button>
        )}
        {error ? <ErrorNotice error={error} /> : null}
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Регистрация"
      lead="Заведём учётную запись и организацию. Замер начнётся после того, как вы укажете бренд."
      footer={
        <>
          Уже есть учётная запись?{' '}
          <Link to="/auth/login" state={from ? { from } : undefined}>
            Войти
          </Link>
        </>
      }
    >
      {error ? <ErrorNotice error={error} /> : null}

      <form onSubmit={submit} noValidate>
        <Field
          id="email"
          label="Рабочая почта"
          hint="На неё придёт письмо со ссылкой подтверждения"
          error={fields.email}
        >
          {(props) => (
            <input
              {...props}
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>

        <Field
          id="full-name"
          label="Как вас зовут"
          hint="Имя видят коллеги по организации"
        >
          {(props) => (
            <input
              {...props}
              type="text"
              name="full_name"
              autoComplete="name"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
            />
          )}
        </Field>

        <Field
          id="organization"
          label="Название организации"
          hint="Появится в отчётах и в приглашениях коллегам. Потом можно изменить"
          error={fields.organization_name}
        >
          {(props) => (
            <input
              {...props}
              type="text"
              name="organization_name"
              autoComplete="organization"
              value={organization}
              onChange={(event) => setOrganization(event.target.value)}
            />
          )}
        </Field>

        <Field
          id="password"
          label="Пароль"
          hint={PASSWORD_HINT}
          error={fields.password ?? shortPassword(password)}
        >
          {(props) => (
            <input
              {...props}
              type="password"
              name="password"
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN_LENGTH}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>

        {/*
          Адреса берутся из `LEGAL`, а не пишутся руками. Здесь стояли два
          зашитых адреса, и оба вели не туда, что обещала подпись: «оферту» — на
          `/terms/` (пользовательское соглашение), «политику обработки
          персональных данных» — на `/privacy/`, где лежало согласие. Вдобавок
          обе ссылки открывались в той же вкладке и стирали наполовину
          заполненную форму регистрации — за документом человек уходил, а
          возвращался к пустым полям. `LEGAL_LINK_PROPS` открывает их рядом.

          Документов три, и это не педантизм. Согласие на обработку прямо
          называет отправку этой формы действием, которым оно даётся, — а ссылки
          на его текст не было вовсе: человек давал согласие, которого не мог
          прочитать. Политика конфиденциальности — отдельный документ и другое
          основание, поэтому и глагол при ней другой: её читают, а не принимают.

          Галочки согласия здесь нет намеренно. Она жила только в браузере:
          в `SignupRequest` контракта признака согласия нет, сервер не получал
          и не хранил ничего — а ч. 4 ст. 9 152-ФЗ возлагает доказывание
          получения согласия на оператора. Отметка, которую нечем подтвердить,
          не доказательство, а его имитация. Пока поле не заведено в
          `seeno-ru-api`, форма честно опирается на само действие: обработка
          данных учётной записи нужна для исполнения договора (п. 5 ч. 1 ст. 6
          152-ФЗ), и строка под кнопкой называет документы, которые человек
          принимает отправкой формы.
        */}
        <span className="hint" id="signup-legal">
          Отправляя форму, вы принимаете{' '}
          <a href={LEGAL.offer} {...LEGAL_LINK_PROPS}>
            оферту
          </a>{' '}
          и{' '}
          <a href={LEGAL.privacy} {...LEGAL_LINK_PROPS}>
            политику конфиденциальности
          </a>
          . Служебные письма про вход и оплату придут на этот адрес.
        </span>

        <button
          className="btn btn-primary"
          type="submit"
          disabled={busy || password.length < PASSWORD_MIN_LENGTH || !email.trim()}
        >
          {busy ? 'Создаём…' : 'Создать учётную запись'}
        </button>
      </form>
    </AuthCard>
  );
}
